// Apolo IDE — OutputsRail (Fase 1 · rail de Saídas) — redesign v2 (Codex-rich).
//
// Coluna direita do Code chat: agrega tudo que o agente PRODUZIU num turno como
// cards clicáveis, separando arquivos escritos de testes que passaram. Lê o
// contrato C2 via `deriveArtifacts(messages)` (lib/artifacts.js) — não entende o
// shape cru das tool messages, só o Artifact[] derivado.
//
// Estilo portado pixel-a-pixel de _mockups/code-chat-v2/redesign.html (classes
// `rx-*` em redesign.css): header `.rx-rail-h` + cards `.rx-out` com ícone
// colorido `.rx-oic` (.rx-blue arquivo / .rx-greenic teste) e `.rx-ot`
// (nome `.rx-n` + meta mono `.rx-m`).
//
//   • kind==='file' → ícone FilePen azul + basename + "editado · +adds −dels"
//     (−dels só quando dels>0). Card é um <button> "Abrir" → onOpen(artifact).
//   • kind==='test' → ícone CircleCheck verde + "teste" + summary ("12 passed").
//   • vazio → estado mudo ("Nenhuma saída ainda…").
//
// Sem seção "Fontes" (web) — adiada até existir tool de web (DECISIONS · D-web).

import { FilePen, CircleCheck, PackageOpen } from 'lucide-react';
import { deriveArtifacts } from '../../../lib/artifacts.js';
import SubagentsRailSection from './SubagentsRailSection.jsx';
import './redesign.css';

function basename(path) {
  const str = String(path ?? '').replace(/\\/g, '/');
  return str.split('/').pop() || str;
}

function fileMeta(artifact) {
  const hasDeletions = artifact.deletions > 0;
  return (
    <>
      editado · <span className="rx-a">+{artifact.additions}</span>
      {hasDeletions && <> <span className="rx-d">−{artifact.deletions}</span></>}
    </>
  );
}

function FileOut({ artifact, onOpen }) {
  const name = basename(artifact.path);
  return (
    <button type="button" className="rx-out" onClick={() => onOpen?.(artifact)} aria-label="Abrir">
      <span className="rx-oic rx-blue">
        <FilePen size={15} aria-hidden="true" />
      </span>
      <div className="rx-ot">
        <div className="rx-n" title={artifact.path}>{name}</div>
        <div className="rx-m">{fileMeta(artifact)}</div>
      </div>
    </button>
  );
}

function TestOut({ artifact }) {
  return (
    <div className="rx-out">
      <span className="rx-oic rx-greenic">
        <CircleCheck size={15} aria-hidden="true" />
      </span>
      <div className="rx-ot">
        <div className="rx-n">teste</div>
        {artifact.summary && (
          <div className="rx-m" title={artifact.summary}>{artifact.summary}</div>
        )}
      </div>
    </div>
  );
}

export default function OutputsRail({ messages, onOpen, sessionId }) {
  const artifacts = deriveArtifacts(messages);

  return (
    <aside aria-label="Saídas" className="rx-rail">
      <div className="rx-rail-h">
        <PackageOpen size={13} aria-hidden="true" />
        Saídas
        <span className="rx-cnt" aria-label={`${artifacts.length} saídas`}>
          {artifacts.length}
        </span>
      </div>

      {artifacts.length === 0 ? (
        <p className="rx-rail-empty">
          Nenhuma saída ainda — o que o agente escrever aparece aqui.
        </p>
      ) : (
        <div className="rx-rail-list">
          {artifacts.map((artifact) =>
            artifact.kind === 'test' ? (
              <TestOut key={artifact.id} artifact={artifact} />
            ) : (
              <FileOut key={artifact.id} artifact={artifact} onOpen={onOpen} />
            ),
          )}
        </div>
      )}

      {/* Fase 2 — seção Subagentes (delta do print #2). Só renderiza com
          sessionId (mantém o teste puro da rail intocado) e some quando a
          sessão não tem subagente. */}
      {sessionId ? <SubagentsRailSection sessionId={sessionId} /> : null}
    </aside>
  );
}
